import { Avatar, Box, ButtonBase, Popover, Typography } from "@mui/material";
import { useState } from "react";

type Assignee = { id: number; firstName: string; lastName: string };

interface AssigneeRowProps {
  assignees: Assignee[];
  maxVisible?: number; // default: 3
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
};

const CHIP_COLORS = [
  { bg: "#CECBF6", color: "#3C3489" },
  { bg: "#9FE1CB", color: "#085041" },
  { bg: "#F5C4B3", color: "#712B13" },
  { bg: "#B5D4F4", color: "#0C447C" },
  { bg: "#FAC775", color: "#633806" },
  { bg: "#C0DD97", color: "#27500A" },
  { bg: "#F4C0D1", color: "#72243E" },
];

const colorFor = (id: number) => CHIP_COLORS[id % CHIP_COLORS.length];

export const AssigneeRow = ({ assignees, maxVisible = 3 }: AssigneeRowProps) => {
  const visible = assignees.slice(0, maxVisible);
  const overflow = assignees.slice(maxVisible);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Box>
      {/* Single-line row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "nowrap", overflow: "hidden" }}>
        {visible.map((a) => {
          const { bg, color } = colorFor(a.id);
          return (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                bgcolor: "background.paper",
                border: "0.5px solid",
                borderColor: "divider",
                borderRadius: "20px",
                px: "8px",
                pl: "3px",
                py: "3px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <Avatar sx={{ width: 22, height: 22, fontSize: "9px", bgcolor: bg, color }}>
                {getInitials(`${a.firstName} ${a.lastName}`)}
              </Avatar>
              <Typography variant="caption" sx={{ color: "text.primary" }}>
                {`${a.firstName} ${a.lastName}`.split(" ").join(" ")}
              </Typography>
            </Box>
          );
        })}

        {overflow.length > 0 && (
          <ButtonBase
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label={`Show ${overflow.length} more items`}
            aria-haspopup="true"
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "action.hover",
              border: "0.5px solid",
              borderColor: "divider",
              borderRadius: "20px",
              padding: "13px 10px",
              fontSize: "12px",
              fontWeight: 500,
              color: "text.secondary",
              cursor: "pointer",
              flexShrink: 0,
              userSelect: "none",
              "&:hover": { bgcolor: "action.selected" },
            }}
          >
            +{overflow.length} more
          </ButtonBase>
        )}
      </Box>

      {/* Popover for overflow assignees */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: "10px 14px",
            minWidth: 220,
            border: "0.5px solid",
            borderColor: "divider",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: 2,
          },
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
          All Assignees
        </Typography>
        {assignees.map((assignee) => {
          const { bg, color } = colorFor(assignee.id);
          return (
            <Box
              key={assignee.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                py: "6px",
                borderBottom: "0.5px solid",
                borderColor: "divider",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <Avatar sx={{ width: 28, height: 28, fontSize: "11px", bgcolor: bg, color }}>
                {getInitials(`${assignee.firstName} ${assignee.lastName}`)}
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {`${assignee.firstName} ${assignee.lastName}`}
              </Typography>
            </Box>
          );
        })}
      </Popover>
    </Box>
  );
};
